import { useState, useEffect } from 'react';
import { devicesApi, Device, DeviceStatus } from '../../api/devices.api';
import { repairFormsApi, RepairForm } from '../../api/repair-forms.api';
import { serviceProvidersApi, ServiceProvider } from '../../api/service-providers.api';
import { generateRepairFormPDF } from '../../utils/pdfGenerator';
import { useBranch } from '../../hooks/useBranch';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const RepairManagement = () => {
  const { activeBranch } = useBranch();
  const [devices, setDevices] = useState<Device[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [repairForm, setRepairForm] = useState<RepairForm | null>(null);
  const [formData, setFormData] = useState({
    notes: '',
    estimatedCost: 0,
    estimatedCompletionDate: '',
  });

  useEffect(() => {
    fetchDevices();
    fetchServiceProviders();
  }, []);

  const fetchServiceProviders = async () => {
    try {
      const data = await serviceProvidersApi.getAll();
      setServiceProviders(data.filter((s) => s.isActive));
    } catch (err: any) {
      console.error('Servisler yüklenirken hata:', err);
    }
  };

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      // Get devices with InRepair status
      const data = await devicesApi.getByStatus(DeviceStatus.InRepair);
      setDevices(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Cihazlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = async (device: Device) => {
    setSelectedDevice(device);
    setFormData({
      notes: device.notes || '',
      estimatedCost: 0,
      estimatedCompletionDate: '',
    });
    
    // Fetch repair form for this device
    try {
      const deviceId = typeof device.id === 'string' ? device.id : (device as any)._id;
      const forms = await repairFormsApi.getByDevice(deviceId);
      if (forms && forms.length > 0) {
        // Get the most recent form
        setRepairForm(forms[0]);
      } else {
        setRepairForm(null);
      }
    } catch (err) {
      console.error('Servis formu yüklenirken hata:', err);
      setRepairForm(null);
    }
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDevice(null);
    setRepairForm(null);
  };

  const handlePrintForm = async () => {
    if (!repairForm || !selectedDevice) return;

    try {
      // Get product name, brand, and model
      let productName = 'Bilinmiyor';
      let brand = '';
      let model = '';
      
      console.log('Selected Device:', selectedDevice);
      console.log('Device Product:', selectedDevice.product);
      console.log('Device Brand:', selectedDevice.brand);
      
      if (typeof selectedDevice.product === 'object' && selectedDevice.product !== null) {
        productName = (selectedDevice.product as any).name || 'Bilinmiyor';
        console.log('Product Name:', productName);
        
        // Extract brand from product
        if ((selectedDevice.product as any).brand) {
          const productBrand = (selectedDevice.product as any).brand;
          console.log('Product Brand Object:', productBrand);
          
          if (typeof productBrand === 'object' && productBrand !== null) {
            brand = productBrand.name || productBrand._id || String(productBrand);
          } else {
            brand = String(productBrand);
          }
          console.log('Extracted Brand:', brand);
        }
        
        // Extract model from product
        if ((selectedDevice.product as any).model) {
          const productModel = (selectedDevice.product as any).model;
          if (typeof productModel === 'object' && productModel !== null) {
            model = productModel.name || String(productModel);
          } else {
            model = String(productModel);
          }
        }
      }
      
      // Fallback to device-level brand and model
      if (!brand && selectedDevice.brand) {
        if (typeof selectedDevice.brand === 'object' && selectedDevice.brand !== null) {
          brand = (selectedDevice.brand as any).name || String(selectedDevice.brand);
        } else {
          brand = String(selectedDevice.brand);
        }
      }
      
      if (!model && selectedDevice.model) {
        if (typeof selectedDevice.model === 'object' && selectedDevice.model !== null) {
          model = (selectedDevice.model as any).name || String(selectedDevice.model);
        } else {
          model = String(selectedDevice.model);
        }
      }

      console.log('Final Brand:', brand);
      console.log('Final Model:', model);

      const service = serviceProviders.find(s => s.id === repairForm.serviceProvider);

      // Generate PDF
      generateRepairFormPDF({
        formNumber: repairForm.id.substring(0, 8).toUpperCase(),
        date: new Date(repairForm.createdAt).toLocaleDateString('tr-TR'),
        deviceInfo: {
          serialNumber: repairForm.serialNumber,
          assetTag: repairForm.assetTag || 'Belirtilmemis',
          productName: productName,
          brand: brand || 'Bilinmiyor',
          model: model || 'Bilinmiyor',
          condition: repairForm.deviceCondition || 'Good',
        },
        serviceProvider: {
          name: repairForm.serviceProviderName,
          contactPerson: service?.contactPerson,
          phone: service?.phone,
          address: service?.address,
        },
        repairDetails: {
          faultDescription: repairForm.faultDescription,
          accessories: repairForm.accessories,
        },
        delivery: {
          deliveryDate: repairForm.deliveryDate,
          deliveredBy: repairForm.deliveredBy,
          receivedBy: repairForm.receivedBy,
        },
        branch: {
          name: activeBranch?.name || 'Sube',
          address: activeBranch?.address,
          phone: activeBranch?.phone,
        },
        notes: repairForm.notes,
      });
    } catch (err: any) {
      setError(err.message || 'PDF oluşturulamadı');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDevice) return;

    try {
      await devicesApi.update(selectedDevice.id, {
        notes: formData.notes,
      });
      await fetchDevices();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const getProductName = (product: any): string => {
    if (typeof product === 'object' && product !== null) {
      return product.name || '-';
    }
    return '-';
  };

  const getServiceProvider = (device: Device): string => {
    if (device.currentLocation?.type === 'Repair' && device.currentLocation?.name) {
      return device.currentLocation.name;
    }
    return '-';
  };

  const getCreatedBy = (device: Device): string => {
    if (device.createdBy) {
      if (typeof device.createdBy === 'object') {
        const user = device.createdBy as any;
        if (user.firstName && user.lastName) {
          return `${user.firstName} ${user.lastName}`;
        }
        return user.email || user.username || '-';
      }
    }
    return '-';
  };

  const getRepairDuration = (device: Device): string => {
    if (device.currentLocation?.since) {
      const since = new Date(device.currentLocation.since);
      const now = new Date();
      const days = Math.floor((now.getTime() - since.getTime()) / (1000 * 60 * 60 * 24));
      return `${days} gün`;
    }
    return '-';
  };

  const getConditionBadge = (condition: string) => {
    const badges: Record<string, string> = {
      New: 'bg-green-100 text-green-800',
      Good: 'bg-blue-100 text-blue-800',
      Fair: 'bg-yellow-100 text-yellow-800',
      Poor: 'bg-orange-100 text-orange-800',
      Broken: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      New: 'Yeni',
      Good: 'İyi',
      Fair: 'Orta',
      Poor: 'Kötü',
      Broken: 'Bozuk',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[condition] || 'bg-gray-100 text-gray-800'}`}>
        {labels[condition] || condition}
      </span>
    );
  };

  const columns: Column<Device>[] = [
    { key: 'serialNumber', header: 'Seri No' },
    { key: 'product', header: 'Ürün', render: (item) => getProductName(item.product) },
    { key: 'assetTag', header: 'Varlık Etiketi', render: (item) => item.assetTag || '-' },
    { key: 'condition', header: 'Durum', render: (item) => getConditionBadge(item.condition) },
    { key: 'serviceProvider', header: 'Servis', render: (item) => getServiceProvider(item) },
    { key: 'duration', header: 'Onarımda', render: (item) => getRepairDuration(item) },
    { key: 'createdBy', header: 'İşlemi Yapan', render: (item) => getCreatedBy(item) },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Detay
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Onarımdaki cihazlar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onarım Yönetimi</h1>
          <p className="text-sm text-gray-600 mt-1">
            Şu anda onarımda olan cihazlar ({devices.length} adet)
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">Toplam Onarımda</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{devices.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">Ortalama Süre</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {devices.length > 0
              ? Math.floor(
                  devices.reduce((sum, d) => {
                    if (d.currentLocation?.since) {
                      const days = Math.floor(
                        (new Date().getTime() - new Date(d.currentLocation.since).getTime()) /
                          (1000 * 60 * 60 * 24)
                      );
                      return sum + days;
                    }
                    return sum;
                  }, 0) / devices.length
                )
              : 0}{' '}
            gün
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">Aktif Servisler</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {new Set(devices.map((d) => d.currentLocation?.id?.toString()).filter(Boolean)).size}
          </div>
        </div>
      </div>

      <Table data={devices} columns={columns} emptyMessage="Şu anda onarımda cihaz bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Onarım Detayları"
        size="lg"
      >
        {selectedDevice && (
          <div>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Cihaz Bilgileri</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Ürün:</span>{' '}
                  <span className="font-medium">{getProductName(selectedDevice.product)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Seri No:</span>{' '}
                  <span className="font-medium">{selectedDevice.serialNumber}</span>
                </div>
                <div>
                  <span className="text-gray-600">Varlık Etiketi:</span>{' '}
                  <span className="font-medium">{selectedDevice.assetTag || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Durum:</span>{' '}
                  {getConditionBadge(selectedDevice.condition)}
                </div>
                <div>
                  <span className="text-gray-600">Servis:</span>{' '}
                  <span className="font-medium">{getServiceProvider(selectedDevice)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Onarımda:</span>{' '}
                  <span className="font-medium">{getRepairDuration(selectedDevice)}</span>
                </div>
              </div>
            </div>

            {repairForm ? (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-blue-900">📋 Servis Teslim Formu</h3>
                  <Button size="sm" onClick={handlePrintForm}>
                    🖨️ Yazdır
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-blue-700">Form No:</span>{' '}
                    <span className="font-medium text-blue-900">{repairForm.id.substring(0, 8).toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Teslim Tarihi:</span>{' '}
                    <span className="font-medium text-blue-900">
                      {new Date(repairForm.deliveryDate).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-blue-700">Arıza Açıklaması:</span>{' '}
                    <span className="font-medium text-blue-900">{repairForm.faultDescription}</span>
                  </div>
                  {repairForm.accessories && (
                    <div className="col-span-2">
                      <span className="text-blue-700">Aksesuarlar:</span>{' '}
                      <span className="font-medium text-blue-900">{repairForm.accessories}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-blue-700">Teslim Eden:</span>{' '}
                    <span className="font-medium text-blue-900">{repairForm.deliveredBy}</span>
                  </div>
                  {repairForm.receivedBy && (
                    <div>
                      <span className="text-blue-700">Teslim Alan:</span>{' '}
                      <span className="font-medium text-blue-900">{repairForm.receivedBy}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Bu cihaz için servis teslim formu bulunamadı.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <Input
                label="Notlar"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Onarım hakkında notlar..."
              />

              <div className="flex gap-2 mt-6">
                <Button type="submit" fullWidth>
                  Güncelle
                </Button>
                <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
                  Kapat
                </Button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RepairManagement;
